import {
  Feature,
  GeoJson,
  Position,
  LineString,
  getLineStringSpeedMetrics,
  LineStringFeatureProperties,
  isSpeedUnreliable,
} from "@shared/primitive-components"
import { Theme } from "@shared/primitive-components/src/theme/theme.types"
import { getColorForSpeedingClassification, speedingClassification } from "."

export const generateRouteFromGeoJson = (
  geoJson: GeoJson
): {
  route: GeoJson
  start: Position | undefined
  end: Position | undefined
  hasUnreliableSpeed: boolean
  bounds: [Position, Position]
} => {
  const [firstFeature, lastFeature] = [geoJson.lineStrings[0], geoJson.lineStrings[geoJson.lineStrings.length - 1]]
  const firstPointCoordinates = firstFeature.geometry.coordinates[0]
  const lastPointCoordinates = lastFeature.geometry.coordinates[lastFeature.geometry.coordinates.length - 1]

  const startPoint = firstPointCoordinates || undefined
  const endPoint = lastPointCoordinates || undefined

  const hasUnreliableSpeed = !!geoJson.lineStrings.find(isSpeedUnreliable)

  const bounds = generateBoundsFromGeoJson(geoJson)

  return {
    route: geoJson,
    start: startPoint,
    end: endPoint,
    hasUnreliableSpeed,
    bounds,
  }
}

export const getSafetyScoreSectionColor = (
  geoJsonFeature: Feature<LineString, LineStringFeatureProperties>,
  theme: Theme
): string => {
  const metrics = getLineStringSpeedMetrics(geoJsonFeature)
  const speedingClass = speedingClassification(metrics)
  return getColorForSpeedingClassification(speedingClass, theme)
}

export const generateBoundsFromGeoJson = (geoJson: GeoJson): [Position, Position] => {
  const bboxCoords = generateBoundingBox(getCoordinatesFromGeoJson(geoJson.lineStrings))
  return bboxCoords
}

function generateBoundingBox(positions: Position[]): [Position, Position] {
  const initialBoundingBox = {
    minLat: positions[0][0],
    maxLat: positions[0][0],
    minLng: positions[0][1],
    maxLng: positions[0][1],
  }

  const boundingBox = positions.reduce(
    (acc, pos) => ({
      minLat: Math.min(acc.minLat, pos[0]),
      maxLat: Math.max(acc.maxLat, pos[0]),
      minLng: Math.min(acc.minLng, pos[1]),
      maxLng: Math.max(acc.maxLng, pos[1]),
    }),
    initialBoundingBox
  )

  const topLeft = [boundingBox.maxLat, boundingBox.minLng]
  const bottomRight = [boundingBox.minLat, boundingBox.maxLng]

  return [topLeft, bottomRight]
}

const getCoordinatesFromGeoJson = (lineStrings: Array<Feature<LineString, LineStringFeatureProperties>>): Position[] =>
  lineStrings.map((x) => x.geometry.coordinates).flat()
